// ab-tracking-pdp.js — PDP event wiring for the new-PDP A/B test.
//
// Loaded on product templates only, from snippets/ab-tracking.liquid. Depends on the core
// (assets/ab-tracking.js) for ssTrack / ssTrackOnce / ssOnLeave.
//
// ─────────────────────────────────────────────────────────────────────────────────────────────
// WHY THERE IS NO "WHICH ARM AM I" BRANCH IN HERE.
//
// Both PDPs' hooks live in this one file, on `main`, and every hook is guarded by the presence of
// its own DOM. The legacy markup never renders in the B theme; main-product.liquid never renders for
// default-template products in control. So presence of the element IS the condition, and the file is
// correct in both themes without knowing which one it is in. The arm label is supplied to GA4 once,
// as a user property, by the core.
//
// It also means nothing here had to be added to sections/template-product.liquid or
// sections/main-product.liquid — everything is delegated listeners and observers. The legacy PDP,
// which is the highest-blast-radius file in the theme, is not touched at all.
//
// ─────────────────────────────────────────────────────────────────────────────────────────────
// TWO OBSERVER CHOICES THAT ARE DELIBERATE.
//
// ZOOM uses a MutationObserver on the overlay's own class, not a click listener on the trigger.
// Both galleries toggle a class on a single overlay element (`zoomed` on control, `is-open` on B),
// so watching that catches EVERY open and close path — trigger, close button, backdrop, Escape —
// without enumerating them per arm. Binding to triggers would have missed closes entirely.
//
// GALLERY DEPTH uses an IntersectionObserver on the slides, not either carousel's API. Control is
// Swiper (transform-based), B is Embla with a native scroll-snap fallback. An IO keyed on the
// scroller reports what is actually on screen regardless of which mechanism moved it, so one
// implementation serves both arms — and it will still work after Swiper is eventually removed.
//
// Plan: docs/AB TEST/PDP/new-pdp-ab-test-plan.md

(function () {
  'use strict';

  if (typeof window.ssTrack !== 'function') return; // core absent: fail quiet, never break the PDP

  // Called through window.* rather than aliased on purpose: aliasing bought nothing and made the
  // whole file undebuggable from the console, because wrapping window.ssTrack after load could not
  // reach a reference captured at load. Now it can.
  function track(n, p) { window.ssTrack(n, p); }
  function once(k, n, p) { window.ssTrackOnce(k, n, p); }
  function onLeave(fn) { window.ssOnLeave(fn); }

  function $(sel) { return document.querySelector(sel); }

  // Mobile = strictly below the 48rem grid breakpoint. Matches the query index.js already uses to
  // build/destroy the legacy Swiper, so "mobile" means the same thing in both arms.
  function isMobile() { return window.matchMedia('(max-width: 47.99rem)').matches; }

  // ── #2 / #9 · You may also like — impression and clicks ────────────────────────────────────
  //
  // The impression doubles as the scroll landmark ("did they get this far down the PDP"). A depth
  // percentage would not have been comparable — the two PDPs are different heights and shapes — so
  // the landmark is content-defined: the recommendations module entering the viewport.
  //
  // It is also #9's denominator. Clicks over PDP views would reward whichever arm happens to place
  // the module higher up the page; clicks over impressions compares people who actually saw it.

  var RECS = ['.section-product-slider', '.pdp__cross-sell'];

  // ── #6 · Cross-sell — impression and clicks ────────────────────────────────────────────────
  //
  // COMPLETE THE STYLE (control) vs STYLED WITH (B). These are NOT the same content: control reads
  // Search & Discovery and falls back to the product's first collection, so it renders on nearly
  // every PDP; B reads a hand-picked metafield with no fallback, so it renders only where one is
  // set. Without the impression denominator, B would be penalised for every page where its module
  // was never on screen — an artefact of catalogue data entry, not of the feature.
  //
  // Item count is evaluated at INTERSECTION time, not at load: B's track is populated by JS.

  var XSELL = [
    { root: '.complementary-products-section-v2', item: '.product-card', module: 'complete_the_style' },
    { root: 'styled-with', item: '[data-qv-open]', module: 'styled_with' }
  ];

  function observeImpression(el, onSeen) {
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { io.disconnect(); onSeen(); return; }
      }
    }, { threshold: 0.25 });
    io.observe(el);
  }

  // A click counts if it lands on something interactive inside the module. Whitespace clicks are
  // not interest; a "View all" click is (and both arms have one, so it stays symmetric).
  function countClicks(root, handler) {
    root.addEventListener('click', function (e) {
      if (e.target.closest('a, button, [data-qv-open]')) handler();
    });
  }

  RECS.forEach(function (sel) {
    var el = $(sel);
    if (!el) return;
    observeImpression(el, function () { once('recs_impression', 'pdp_recs_impression'); });
    countClicks(el, function () { track('pdp_recs_click'); });
  });

  XSELL.forEach(function (cfg) {
    var el = $(cfg.root);
    if (!el) return;
    observeImpression(el, function () {
      if (!el.querySelector(cfg.item)) return; // rendered but empty — not an impression
      once('xsell_impression', 'pdp_crosssell_impression', { pdp_module: cfg.module });
    });
    countClicks(el, function () { track('pdp_crosssell_click', { pdp_module: cfg.module }); });
  });

  // ── #3 · Size guide ────────────────────────────────────────────────────────────────────────
  // Control opens a modal via the theme's generic [data-modaltarget] delegation; B uses ss-modal's
  // [data-ss-modal-open] on a .size-guide-trigger button. Delegated on document so neither needs
  // its own file touched.

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-modaltarget="size-chart"], .size-guide-trigger')) {
      track('pdp_size_guide_open');
    }
  });

  // ── #4 · Product details accordion — B only ────────────────────────────────────────────────
  //
  // Native <details> rows, so `toggle` is the event and `.open` tells us which direction.
  //
  // The row label comes from a merchant-editable setting, so the value is slugified from the
  // heading text rather than hardcoded. Consequence worth knowing: renaming a label in the theme
  // editor starts a NEW value in GA4 rather than continuing the old one. That was preferable to
  // adding data attributes to blocks/product-details.liquid for tracking's sake.

  function slug(s) {
    return (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
  }

  var pd = $('product-details');
  if (pd) {
    pd.querySelectorAll('details.ss-acc__item').forEach(function (row) {
      row.addEventListener('toggle', function () {
        if (!row.open) return; // opens only; a close is not a second interaction
        var heading = row.querySelector('.ss-acc__heading');
        track('pdp_accordion_open', { pdp_section: slug(heading && heading.textContent) });
      });
    });
  }

  // ── #5 · See it in your size — B only ──────────────────────────────────────────────────────
  //
  // `available` is the denominator and it matters: not every product has the metaobject data, so
  // opens over all PDP views would read as low engagement when it is really low coverage.

  if ($('.siiys-trigger')) {
    once('siiys_available', 'pdp_siiys_available');
    document.addEventListener('click', function (e) {
      if (e.target.closest('.siiys-trigger')) track('pdp_siiys_open');
      var sizeBtn = e.target.closest('[data-size-btn]');
      if (sizeBtn) track('pdp_siiys_size_click', { pdp_size: sizeBtn.getAttribute('data-size-btn') });
    });
  }

  // ── #7 / #8 · Quick view — B only ──────────────────────────────────────────────────────────
  //
  // The drawer is rendered only from sections/main-product.liquid, so this whole block is inert in
  // control. Note the collection page's lookbook drawer is a DIFFERENT component and is not counted
  // here.
  //
  // The open event fires on the same click as pdp_crosssell_click. That is intended — one measures
  // "the cross-sell attracted a click", the other "the quick view was opened" — and the two counts
  // agreeing is a useful sanity check.
  //
  // ATC is scoped to inside the drawer: the PDP's own buy box uses the same .bb__atc class.

  //
  // CAPTURE PHASE, and it has to be. UpCart intercepts the drawer's add-to-cart button and stops the
  // click from propagating, so a normal (bubble-phase) listener on document never sees it —
  // pdp_quickview_open and pdp_quickview_goto_product arrived while pdp_quickview_atc silently did
  // not. Capture runs on the way DOWN, before any handler can cancel propagation.
  //
  // Verified: `document.querySelector('quick-view-drawer .bb__atc')` is truthy while the drawer is
  // open, so the element was always there — it was the event that never arrived. An earlier "proof"
  // using an injected bare button passed precisely because it had no UpCart binding on it.
  if ($('quick-view-drawer')) {
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-qv-open]')) track('pdp_quickview_open');

      var inDrawer = e.target.closest('quick-view-drawer');
      if (!inDrawer) return;
      if (e.target.closest('.bb__atc')) track('pdp_quickview_atc');
      if (e.target.closest('.qv__pdp-link')) track('pdp_quickview_goto_product');
    }, true);
  }

  // ── #10 / #11 · Zoom — open, and time spent inside ─────────────────────────────────────────
  //
  // Duration is measured, not read from GA4 — there is no native "time in a UI state" metric.
  //
  // Two things make the number trustworthy. Backgrounded time is NOT counted: the clock pauses when
  // the tab is hidden, or someone who opens the zoom and switches apps for ten minutes lands a
  // ten-minute "engagement". And the duration is flushed if the visitor leaves without closing —
  // which is most of them, and they are the longest sessions, so a close-only listener would have
  // systematically discarded the best data.

  function wireZoom(sel, openClass) {
    var el = $(sel);
    if (!el) return;

    var isOpen = false, startedAt = 0, accrued = 0;

    function startClock() { startedAt = performance.now(); }
    function stopClock() {
      if (!startedAt) return;
      accrued += performance.now() - startedAt;
      startedAt = 0;
    }
    function flush() {
      if (!isOpen) return;
      stopClock();
      isOpen = false;
      track('pdp_zoom_close', { zoom_duration_ms: Math.round(accrued) });
      accrued = 0;
    }

    new MutationObserver(function () {
      var nowOpen = el.classList.contains(openClass);
      if (nowOpen && !isOpen) {
        isOpen = true;
        accrued = 0;
        startClock();
        track('pdp_zoom_open');
      } else if (!nowOpen && isOpen) {
        flush();
      }
    }).observe(el, { attributes: true, attributeFilter: ['class'] });

    document.addEventListener('visibilitychange', function () {
      if (!isOpen) return;
      if (document.visibilityState === 'hidden') stopClock();
      else startClock();
    });

    // Leaving with the zoom still open: bank what we have. ssOnLeave is once-only, so a visitor who
    // backgrounds and returns is not double-counted — we simply stop measuring that open.
    onLeave(flush);
  }

  wireZoom('#product-media-zoom', 'zoomed');    // control
  wireZoom('product-gallery-zoom', 'is-open');  // B

  // ── #12 · Mobile gallery depth ─────────────────────────────────────────────────────────────
  //
  // Sent on leave, once, for every mobile PDP view that has a gallery — including views where the
  // visitor never moved past the first image. Keeping those in is what makes the rates well
  // defined: completion % averages honestly and "reached the end" is a share of all views, not of
  // some self-selected engaged subset.
  //
  // Comparable between arms only because the legacy gallery's autoplay was disabled for this test
  // (assets/index.js). With it on, control's carousel advanced itself to the last image in ~45s
  // whether or not anyone was watching.

  function wireGalleryDepth(rootSel, itemSel) {
    if (!isMobile() || !('IntersectionObserver' in window)) return;
    var root = $(rootSel);
    if (!root) return;
    var items = root.querySelectorAll(itemSel);
    if (!items.length) return;

    var total = items.length;
    var maxIndex = 0;

    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].intersectionRatio < 0.5) continue;
        var idx = Number(entries[i].target.getAttribute('data-ab-idx'));
        if (idx > maxIndex) maxIndex = idx;
      }
    }, { root: root, threshold: [0.5] });

    Array.prototype.forEach.call(items, function (el, i) {
      el.setAttribute('data-ab-idx', String(i));
      io.observe(el);
    });

    onLeave(function () {
      track('pdp_gallery_depth', {
        gallery_max_index: maxIndex,
        gallery_total: total,
        gallery_completion_pct: Math.round(((maxIndex + 1) / total) * 100)
      });
      if (maxIndex >= total - 1) track('pdp_gallery_reached_end');
    });
  }

  wireGalleryDepth('.swiper-product-images', '[data-media-item]'); // control
  wireGalleryDepth('.pg__viewport', '.pg__item');                  // B
})();
